/**
 * React bindings for the photo outbox.
 *
 *   usePhotoOutbox()  → { pending, sync, syncing }
 *   usePhotoOutboxSync() (mount once in App) → flushes on online / focus / interval
 */
import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  flushAll,
  listPendingPhotos,
  pendingPhotoCount,
  subscribePhotoOutbox,
  type PhotoOutboxEntry,
} from '@/lib/offline/photo-outbox'

// A cheap live "count" store. Uses useSyncExternalStore so multiple mounts
// share the same subscription and re-render together.
let cachedCount = 0
let firstReadStarted = false
const countListeners = new Set<() => void>()

function primeCount() {
  if (firstReadStarted) return
  firstReadStarted = true
  pendingPhotoCount()
    .then((n) => {
      cachedCount = n
      countListeners.forEach((cb) => cb())
    })
    .catch(() => {
      /* IDB not available — leave at 0 */
    })
}

// Refresh cached count on every outbox change.
subscribePhotoOutbox(() => {
  pendingPhotoCount()
    .then((n) => {
      cachedCount = n
      countListeners.forEach((cb) => cb())
    })
    .catch(() => {
      /* ignore */
    })
})

function subscribeCount(cb: () => void): () => void {
  countListeners.add(cb)
  primeCount()
  return () => countListeners.delete(cb)
}

/** Live count of pending photos. Reactive across the app. */
export function usePendingPhotoCount(): number {
  return useSyncExternalStore(subscribeCount, () => cachedCount, () => 0)
}

/** Full list of pending entries, refreshed on outbox change. */
export function usePendingPhotos(): {
  entries: PhotoOutboxEntry[]
  refresh: () => void
} {
  const [entries, setEntries] = useState<PhotoOutboxEntry[]>([])
  const refresh = () => {
    listPendingPhotos()
      .then(setEntries)
      .catch(() => setEntries([]))
  }
  useEffect(() => {
    refresh()
    const unsub = subscribePhotoOutbox(refresh)
    return () => {
      unsub()
    }
  }, [])
  return { entries, refresh }
}

/**
 * Mount once at app root. Flushes the outbox on `online` / `focus` /
 * `visibilitychange` and on a slow 60s interval — none of these are
 * expensive on an empty queue.
 */
export function usePhotoOutboxSync(): void {
  const inFlightRef = useRef(false)
  const queryClient = useQueryClient()

  useEffect(() => {
    let cancelled = false

    const run = async () => {
      if (inFlightRef.current) return
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return
      const count = await pendingPhotoCount().catch(() => 0)
      if (count === 0) return
      inFlightRef.current = true
      try {
        const result = await flushAll()
        if (result.sent > 0) {
          // A photo lands in a row's photos array; the list/detail queries
          // that render those photos won't refetch on their own. Invalidate
          // active queries broadly — it's rare and photos are read-heavy.
          queryClient.invalidateQueries({ refetchType: 'active' })
        }
      } catch {
        /* individual entry errors already recorded in the outbox */
      } finally {
        if (!cancelled) inFlightRef.current = false
      }
    }

    const onOnline = () => run()
    const onFocus = () => run()
    const onVis = () => {
      if (document.visibilityState === 'visible') run()
    }

    window.addEventListener('online', onOnline)
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVis)
    const interval = setInterval(run, 60_000)

    // Also react to outbox notifications from callers (enqueue / manual retry).
    const unsubOutbox = subscribePhotoOutbox(() => {
      // Fire-and-forget — the guard above prevents overlap.
      run()
    })

    // Initial pass in case the app started with queued photos already.
    run()

    return () => {
      cancelled = true
      window.removeEventListener('online', onOnline)
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVis)
      clearInterval(interval)
      unsubOutbox()
    }
  }, [queryClient])
}
